import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoginService } from './services/login.service';
import { QuickAddService } from './services/quick-add.service';
import { Router } from '@angular/router';
import { AppCommon } from './providers/app-common';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadChangeParam, NzUploadFile } from 'ng-zorro-antd/upload';
import { ExternalViewModel } from './models-views/external.view.model';
import { ExternalDeskFeedbackPayloadModel } from './models/external-feedback.model';
 
type FeedbackScreenshotMode = 'none' | 'generic' | 'crop';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss',
 
})
export class AppComponent {

  // title = 'bf-insight360';
  isCollapsed = false;
  isLoggedIn = false;
  selectedQucik:unknown;
  drawerData:any;
  isExternalPage = false; 
  feedbackVisible = false;
  feedbackScreenshotVisible = false;
  feedbackLoading = false;
  feedbackForm: FormGroup;
  feedbackFileList: NzUploadFile[] = [];
  feedbackScreenshots: string[] = [];
  feedbackScreenshotMode: FeedbackScreenshotMode = 'none';
  feedbackCapturedImage = '';
  isFeedbackCapturing = false;
  isCropMode = false;
  cropStartPoint: { x: number; y: number } | null = null;
  cropSelection = { x: 0, y: 0, width: 0, height: 0 };

  constructor(private loginService: LoginService, private quickAddService: QuickAddService,
    private router: Router,
    private fb: FormBuilder,
    private externalViewModel: ExternalViewModel,
    private message: NzMessageService,
  ){
    this.feedbackForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      title: ['', [Validators.required]],
      type: [2, [Validators.required]],
      comment: ['', [Validators.required]]
    });
    //  this.quickAddService.data.subscribe(e => e && ( this.drawerData = e))
    this.subscribeToIsLoggedin();
    this.subscribeToQuickAdd();
    
  }

  ngOnInit(): void {
 
    if (window.location.href.includes('/external/')) {
      this.isExternalPage = true;
    }
  }

  onCollapse(e:any){
    console.log(e)
    this.quickAddService.setCollapsed(e)
  }

  subscribeToQuickAdd(){
    this.quickAddService.currentState.subscribe((e: 'closed' | 'client' | 'project' | 'task' | 'event') => {
        this.selectedQucik = e;
        this.drawerData = this.quickAddService.getData();
    });
  }

  subscribeToIsLoggedin(){
    this.loginService.loginChanged.subscribe(isLoggedIn => {
      this.isLoggedIn = isLoggedIn;
    });
  }

  openQuickApp(type: 'client' | 'project' | 'task' | 'event'){
    this.quickAddService.openQuickAdd(type);
  }

  openFeedbackModal(): void {
    this.resetFeedbackForm();
    this.prefillFeedbackUser();
    this.feedbackScreenshotVisible = true;
  }

  closeFeedbackModal(): void {
    if (this.feedbackLoading) {
      return;
    }
    this.feedbackVisible = false;
  }

  closeFeedbackScreenshotModal(): void {
    this.feedbackScreenshotVisible = false;
  }

  async selectFeedbackScreenshotMode(mode: FeedbackScreenshotMode): Promise<void> {
    this.feedbackScreenshotMode = mode;
    this.feedbackScreenshots = [];
    this.feedbackFileList = [];

    if (mode === 'none') {
      this.feedbackScreenshotVisible = false;
      this.feedbackVisible = true;
      return;
    }

    this.feedbackScreenshotVisible = false;
    await this.waitForUiToSettle();

    try {
      if (mode === 'generic') {
        const screenshot = await this.captureWebpageImage();
        this.setFeedbackScreenshot(screenshot, 'feedback-screen.png');
        this.feedbackVisible = true;
        return;
      }

      const screenshot = await this.captureWebpageImage();
      this.feedbackCapturedImage = screenshot;
      this.startCropMode();
    } catch (error) {
      this.message.error('Could not capture the webpage screenshot');
      this.feedbackScreenshotVisible = true;
    }
  }

  submitFeedback(): void {
    if (this.feedbackForm.invalid) {
      Object.values(this.feedbackForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      this.message.error('Please fill all required feedback fields correctly');
      return;
    }

    const formValue = this.feedbackForm.getRawValue();
    const payload: ExternalDeskFeedbackPayloadModel = {
      name: formValue.name,
      email: formValue.email,
      title: formValue.title,
      comment: formValue.comment,
      type: Number(formValue.type),
      screenshot: this.feedbackScreenshots
    };

    this.feedbackLoading = true;
    this.externalViewModel.createExternalDeskFeedback(
      payload,
      () => {
        this.feedbackLoading = false;
        this.message.success('Feedback sent successfully');
        this.resetFeedbackForm();
        this.feedbackVisible = false;
        return '';
      },
      (error: any) => {
        this.feedbackLoading = false;
        this.message.error(this.getFeedbackErrorMessage(error));
        return '';
      }
    );
  }

  beforeFeedbackImageUpload = (file: NzUploadFile): boolean => {
    const rawFile = file as unknown as File;
    if (!rawFile.type?.startsWith('image/')) {
      this.message.error('Please upload image files only');
      return false;
    }

    if (rawFile.size / 1024 / 1024 > 5) {
      this.message.error(`${rawFile.name} is larger than 5MB`);
      return false;
    }

    this.fileToBase64(rawFile).then(base64 => {
      const uploadFile: NzUploadFile = {
        uid: file.uid || `${Date.now()}-${this.feedbackFileList.length}`,
        name: rawFile.name,
        status: 'done',
        url: base64,
        thumbUrl: base64,
        originFileObj: rawFile as any
      };
      this.feedbackFileList = [...this.feedbackFileList, uploadFile];
      this.feedbackScreenshots = [...this.feedbackScreenshots, base64];
    }).catch(() => {
      this.message.error(`Failed to read ${rawFile.name}`);
    });

    return false;
  }

  onFeedbackUploadChange(event: NzUploadChangeParam): void {
    if (event.type !== 'removed') {
      return;
    }
    this.feedbackFileList = this.feedbackFileList.filter(file => file.uid !== event.file.uid);
    this.feedbackScreenshots = this.feedbackFileList.map(file => file.url || file.thumbUrl || '').filter(Boolean);
  }

  startCropSelection(event: MouseEvent): void {
    if (!this.isCropMode) {
      return;
    }
    this.cropStartPoint = { x: event.clientX, y: event.clientY };
    this.cropSelection = { x: event.clientX, y: event.clientY, width: 0, height: 0 };
  }

  updateCropSelection(event: MouseEvent): void {
    if (!this.cropStartPoint) {
      return;
    }

    const x = Math.min(this.cropStartPoint.x, event.clientX);
    const y = Math.min(this.cropStartPoint.y, event.clientY);
    const width = Math.abs(event.clientX - this.cropStartPoint.x);
    const height = Math.abs(event.clientY - this.cropStartPoint.y);
    this.cropSelection = { x, y, width, height };
  }

  finishCropSelection(): void {
    if (!this.cropStartPoint) {
      return;
    }

    this.cropStartPoint = null;
    if (this.cropSelection.width < 12 || this.cropSelection.height < 12) {
      return;
    }

    this.cropCapturedImage().then(croppedImage => {
      this.setFeedbackScreenshot(croppedImage, 'feedback-crop.png');
      this.closeCropMode();
      this.feedbackVisible = true;
    }).catch(() => {
      this.message.error('Could not crop the selected screenshot');
      this.closeCropMode();
      this.feedbackScreenshotVisible = true;
    });
  }

  cancelCropMode(): void {
    this.closeCropMode();
    this.feedbackScreenshotVisible = true;
  }

  private prefillFeedbackUser(): void {
    const user = AppCommon.getloggedInObject()?.user;
    const fullName = [user?.first_name, user?.middlename, user?.last_name].filter(Boolean).join(' ').trim()
      || user?.name
      || user?.username
      || '';

    this.feedbackForm.patchValue({
      name: fullName,
      email: user?.email || ''
    });
  }

  private resetFeedbackForm(): void {
    this.feedbackFileList = [];
    this.feedbackScreenshots = [];
    this.feedbackCapturedImage = '';
    this.feedbackScreenshotMode = 'none';
    this.isFeedbackCapturing = false;
    this.closeCropMode();
    this.feedbackForm.reset({ type: 2 });
    this.prefillFeedbackUser();
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private getFeedbackErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }
    if (Array.isArray(error)) {
      return error.join(', ');
    }
    return error?.message || error?.detail || 'Failed to send feedback. Please try again.';
  }

  private setFeedbackScreenshot(image: string, name: string): void {
    this.feedbackScreenshots = [image];
    this.feedbackFileList = [{
      uid: `${Date.now()}`,
      name,
      status: 'done',
      url: image,
      thumbUrl: image
    }];
  }

  private async captureScreenImage(): Promise<string> {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new Error('Screen capture is not supported');
    }

    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await this.waitForVideoFrame(video);

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      stream.getTracks().forEach(track => track.stop());
      throw new Error('Canvas is not available');
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    stream.getTracks().forEach(track => track.stop());
    return canvas.toDataURL('image/png');
  }

  private async captureWebpageImage(): Promise<string> {
    this.isFeedbackCapturing = true;
    await this.waitForUiToSettle();

    try {
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      const documentElement = document.documentElement;
      const body = document.body;
      const width = Math.max(documentElement.scrollWidth, body.scrollWidth, window.innerWidth);
      const height = Math.max(documentElement.scrollHeight, body.scrollHeight, window.innerHeight);
      const canvas = await html2canvas(body, {
        backgroundColor: '#ffffff',
        height,
        logging: false,
        scale: Math.min(window.devicePixelRatio || 1, 2),
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        width,
        windowHeight: height,
        windowWidth: width
      });

      return canvas.toDataURL('image/png');
    } finally {
      this.isFeedbackCapturing = false;
    }
  }

  private waitForVideoFrame(video: HTMLVideoElement): Promise<void> {
    return new Promise(resolve => {
      if ('requestVideoFrameCallback' in video) {
        (video as any).requestVideoFrameCallback(() => resolve());
        return;
      }
      setTimeout(() => resolve(), 250);
    });
  }

  private waitForUiToSettle(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 200));
  }

  private startCropMode(): void {
    this.isCropMode = true;
    this.cropStartPoint = null;
    this.cropSelection = { x: 0, y: 0, width: 0, height: 0 };
  }

  private closeCropMode(): void {
    this.isCropMode = false;
    this.cropStartPoint = null;
    this.cropSelection = { x: 0, y: 0, width: 0, height: 0 };
  }

  private cropCapturedImage(): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const scaleX = image.naturalWidth / window.innerWidth;
        const scaleY = image.naturalHeight / window.innerHeight;
        const sourceX = this.cropSelection.x * scaleX;
        const sourceY = this.cropSelection.y * scaleY;
        const sourceWidth = this.cropSelection.width * scaleX;
        const sourceHeight = this.cropSelection.height * scaleY;

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(sourceWidth));
        canvas.height = Math.max(1, Math.round(sourceHeight));
        const context = canvas.getContext('2d');
        if (!context) {
          reject();
          return;
        }

        context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      };
      image.onerror = reject;
      image.src = this.feedbackCapturedImage;
    });
  }

  close(){
    this.selectedQucik = undefined
    this.drawerData = undefined
    this.quickAddService.closeQuickAdd()
  }

  onEventCreatedAndNavigate(eventId: number) {
    // Close the drawer and navigate to event details page
    this.close();
    this.router.navigate(['app/events', eventId]);
  }
  

}
